import { useRef } from "react";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { useMedia } from "@/context/MediaContext";
import { MediaFrame } from "@/components/media/MediaFrame";
import { Eyebrow, Reveal } from "@/components/site/Reveal";

const STATEMENTS = [
  "The product being made.",
  "The 5am starts.",
  "The warehouse nobody sees.",
  "The founder who knows everything about the industry.",
  "The process customers never knew existed.",
];

const Line = ({ text, progress, i, total, reduced }) => {
  const start = 0.12 + (i / total) * 0.5;
  const opacity = useTransform(progress, [start, start + 0.08], [0.12, 1]);
  const x = useTransform(progress, [start, start + 0.08], [reduced ? 0 : 24, 0]);
  return (
    <motion.p style={reduced ? undefined : { opacity, x }} className="font-display text-2xl sm:text-3xl md:text-4xl lg:text-5xl tracking-tight leading-[1.1] text-[#2E1B16]">
      {text}
    </motion.p>
  );
};

// Scroll-driven storytelling: statements light up one by one, then the payoff line takes over.
const CoreIdea = () => {
  const ref = useRef(null);
  const reduced = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const mediaY = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);
  const { items } = useMedia("idea", 1);

  return (
    <section ref={ref} className="relative py-32 md:py-48 overflow-hidden" data-testid="core-idea" aria-labelledby="core-idea-heading">
      <motion.div style={{ y: reduced ? 0 : mediaY }} className="absolute inset-y-0 right-0 w-full lg:w-[42%] opacity-[0.28] lg:opacity-40 pointer-events-none" aria-hidden="true">
        <div className="absolute inset-0 [mask-image:linear-gradient(to_right,transparent,black_30%,black_70%,transparent)] lg:[mask-image:linear-gradient(to_right,transparent,black_35%)]">
          <MediaFrame item={items[0]} ratio="h-full" autoplay hoverPlay={false} className="h-full grayscale-[30%]" />
        </div>
      </motion.div>

      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        <Reveal><Eyebrow>The part you don't see</Eyebrow></Reveal>
        <Reveal delay={0.05}>
          <h2 id="core-idea-heading" className="mt-7 font-display uppercase text-4xl sm:text-5xl lg:text-7xl tracking-tight leading-[0.95] text-[#2E1B16] max-w-4xl">
            Most businesses are sitting on their best content.
          </h2>
        </Reveal>

        <div className="mt-20 md:mt-28 grid lg:grid-cols-[1fr_1.1fr] gap-12 lg:gap-24">
          <div className="space-y-6 md:space-y-8 lg:pr-10" data-testid="core-idea-statements">
            {STATEMENTS.map((s, i) => <Line key={s} text={s} i={i} total={STATEMENTS.length} progress={scrollYProgress} reduced={reduced} />)}
          </div>

          <div className="lg:pt-32">
            <Reveal>
              <p className="text-lg md:text-xl text-[#2E1B16]/60 max-w-md leading-relaxed">
                You see it every day, so it doesn't feel interesting anymore.
              </p>
            </Reveal>
            <motion.p initial={{ opacity: 0, scale: 0.96, y: 24 }} whileInView={{ opacity: 1, scale: 1, y: 0 }} viewport={{ once: true, margin: "-120px" }} transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
              className="mt-10 md:mt-14 font-accent text-6xl sm:text-7xl md:text-8xl lg:text-[7.5rem] leading-[0.9] text-[#2E1B16] origin-left" data-testid="core-idea-payoff">
              To your customer,<br />
              <span className="text-[#38BDF8]">it is.</span>
            </motion.p>
            <Reveal delay={0.1}>
              <div className="mt-12 md:mt-16 flex items-start gap-6">
                <span className="mt-3 h-px w-12 bg-[#2E1B16]/30 shrink-0" aria-hidden="true" />
                <p className="text-lg md:text-xl text-[#2E1B16]/70 max-w-md leading-relaxed">
                  Our job is to find those stories and turn them into content that makes people stop, watch and remember your business.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CoreIdea;
